import { useState, useEffect } from "react";
import axios from "axios";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Warehouse, Building2, MapPin, Save, RefreshCw, AlertCircle } from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const TYPE_ICON = { store: Building2, warehouse: Warehouse, yard: MapPin, site: MapPin };

/**
 * Per-product, per-location stock & bin editor. Only rendered when the
 * warehouse module is enabled (parent gates this).
 */
export default function LocationsStockTab({ productId, productName }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [rows, setRows] = useState([]);

  const fetchRows = async () => {
    if (!productId) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/locations/product/${productId}/stock`);
      setRows(res.data.items || []);
    } catch (e) {
      toast.error("Failed to load location stock: " + (e.response?.data?.detail || e.message));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchRows(); /* eslint-disable-next-line */ }, [productId]);

  const updateRow = (locId, field, value) => {
    setRows(rs => rs.map(r => r.location_id === locId ? { ...r, [field]: value, _dirty: true } : r));
  };

  const saveRow = async (row) => {
    setSaving(true);
    try {
      await axios.patch(`${API}/locations/${row.location_id}/stock/${productId}`, {
        product_id: productId,
        location_id: row.location_id,
        reorder_level: row.reorder_level === "" || row.reorder_level == null ? null : Number(row.reorder_level),
        max_level: row.max_level === "" || row.max_level == null ? null : Number(row.max_level),
        bin_location: row.bin_location || "",
      });
      toast.success(`${row.location_code} saved`);
      setRows(rs => rs.map(r => r.location_id === row.location_id ? { ...r, _dirty: false } : r));
    } catch (e) {
      toast.error("Save failed: " + (e.response?.data?.detail || e.message));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="h-6 w-6 animate-spin text-purple-600" />
      </div>
    );
  }

  if (rows.length === 0) {
    return (
      <div className="text-center py-12 text-gray-500">
        <AlertCircle className="h-10 w-10 mx-auto mb-3 text-amber-500" />
        No active locations configured.
      </div>
    );
  }

  const totalStock = rows.reduce((sum, r) => sum + Number(r.current_stock || 0), 0);
  const totalReserved = rows.reduce((sum, r) => sum + Number(r.reserved_stock || 0), 0);

  return (
    <div className="space-y-4" data-testid="locations-stock-tab">
      <div className="bg-purple-50 border border-purple-200 rounded p-3 text-sm">
        <div className="font-semibold text-purple-900">{productName}</div>
        <div className="text-purple-700 mt-1">
          Total on hand across all locations: <span className="font-mono font-bold">{totalStock.toFixed(2)}</span>
          {totalReserved > 0 && <> · Reserved: <span className="font-mono">{totalReserved.toFixed(2)}</span></>}
        </div>
      </div>

      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Code</TableHead>
            <TableHead>Location</TableHead>
            <TableHead className="text-right">On Hand</TableHead>
            <TableHead className="text-right">Reserved</TableHead>
            <TableHead className="w-24">Reorder Lvl</TableHead>
            <TableHead className="w-24">Max Lvl</TableHead>
            <TableHead className="w-32">Bin</TableHead>
            <TableHead className="w-16 text-right"></TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((r) => {
            const Icon = TYPE_ICON[r.location_type] || MapPin;
            return (
              <TableRow key={r.location_id} data-testid={`loc-stock-${r.location_code}`}>
                <TableCell className="font-mono font-semibold">{r.location_code}</TableCell>
                <TableCell className="flex items-center gap-2">
                  <Icon className="h-4 w-4 text-purple-600" />
                  {r.location_name}
                  {r.is_default && <span className="text-[10px] bg-purple-100 text-purple-700 px-1 rounded">DEFAULT</span>}
                </TableCell>
                <TableCell className="text-right font-mono">{Number(r.current_stock || 0).toFixed(2)}</TableCell>
                <TableCell className="text-right font-mono text-amber-700">{Number(r.reserved_stock || 0).toFixed(2)}</TableCell>
                <TableCell>
                  <Input
                    type="number"
                    step="any"
                    value={r.reorder_level ?? ""}
                    onChange={(e) => updateRow(r.location_id, "reorder_level", e.target.value)}
                    placeholder="—"
                    className="h-8 text-sm"
                    data-testid={`reorder-${r.location_code}`}
                  />
                </TableCell>
                <TableCell>
                  <Input
                    type="number"
                    step="any"
                    value={r.max_level ?? ""}
                    onChange={(e) => updateRow(r.location_id, "max_level", e.target.value)}
                    placeholder="—"
                    className="h-8 text-sm"
                  />
                </TableCell>
                <TableCell>
                  <Input
                    value={r.bin_location || ""}
                    onChange={(e) => updateRow(r.location_id, "bin_location", e.target.value.toUpperCase())}
                    placeholder="A-01-1"
                    className="h-8 text-sm font-mono"
                    data-testid={`bin-${r.location_code}`}
                  />
                </TableCell>
                <TableCell className="text-right">
                  <Button
                    size="sm"
                    variant={r._dirty ? "default" : "outline"}
                    disabled={saving || !r._dirty}
                    onClick={() => saveRow(r)}
                    data-testid={`save-loc-${r.location_code}`}
                  >
                    <Save className="h-3 w-3" />
                  </Button>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>

      <div className="text-xs text-gray-500 pt-2 border-t">
        <strong>Bin format:</strong> <span className="font-mono">Aisle-Bay-Level</span>{" "}
        (e.g. <span className="font-mono">A-12-3</span> = Aisle A, Bay 12, Shelf level 3).{" "}
        Use <span className="font-mono">RCV</span>, <span className="font-mono">DISP</span>, or{" "}
        <span className="font-mono">BULK-01</span> for special areas.
      </div>
    </div>
  );
}
